import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface CommissionConfigDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId?: string;
  clubId?: string;
  onSuccess?: () => void;
}

export const CommissionConfigDialog: React.FC<CommissionConfigDialogProps> = ({
  isOpen,
  onOpenChange,
  organizationId,
  clubId,
  onSuccess
}) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    payment_type: 'club_fees',
    platform_commission_rate: '0.03',
    club_commission_rate: '0.00'
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('commission_configs')
        .insert({
          organization_id: organizationId || null,
          club_id: clubId || null,
          payment_type: formData.payment_type,
          platform_commission_rate: parseFloat(formData.platform_commission_rate),
          club_commission_rate: parseFloat(formData.club_commission_rate)
        });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Commission configuration saved successfully'
      });

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Error saving commission config:', error);
      toast({
        title: 'Error',
        description: 'Failed to save commission configuration',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Configure Commission Rates</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="payment_type">Payment Type</Label>
            <Select
              value={formData.payment_type}
              onValueChange={(value) => 
                setFormData(prev => ({ ...prev, payment_type: value }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="club_fees">Club Fees</SelectItem>
                <SelectItem value="coach_salary">Coach Salary</SelectItem>
                <SelectItem value="coach_clinic">Coach Clinic</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="platform_rate">Platform Commission Rate (%)</Label>
            <Input
              id="platform_rate"
              type="number"
              step="0.0001"
              min="0"
              max="1"
              value={formData.platform_commission_rate}
              onChange={(e) => 
                setFormData(prev => ({ ...prev, platform_commission_rate: e.target.value }))
              }
              placeholder="0.03"
            />
          </div>

          {formData.payment_type === 'coach_clinic' && (
            <div>
              <Label htmlFor="club_rate">Club Commission Rate (%)</Label>
              <Input
                id="club_rate"
                type="number"
                step="0.0001"
                min="0"
                max="1"
                value={formData.club_commission_rate}
                onChange={(e) => 
                  setFormData(prev => ({ ...prev, club_commission_rate: e.target.value }))
                }
                placeholder="0.20"
              />
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save Configuration'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};